import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Play, Pause, Volume2, VolumeX, Settings, X, 
  Waves, Wind, Brain, Mic, MessageCircle,
  ChevronDown, ChevronUp, RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audio context for Web Audio API (binaural beats)
let audioContext = null;

const AudioEngine = ({ 
  sessionConfig, 
  affirmations = [],
  onPhaseChange,
  onComplete,
  onClose,
  challengeContext = null
}) => {
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('arrival');
  const [currentTime, setCurrentTime] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  
  // Layer volumes
  const [volumes, setVolumes] = useState({
    ambient: 0.3,
    binaural: 0.25,
    subliminal: 0.08,
    voice: 0.85,
    guidance: 0.9
  });
  
  // Current affirmation
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  
  // Audio refs
  const ambientRef = useRef(null);
  const binauralOscillatorL = useRef(null);
  const binauralOscillatorR = useRef(null);
  const binauralGainL = useRef(null);
  const binauralGainR = useRef(null);
  const timerRef = useRef(null);
  const affirmationTimerRef = useRef(null);
  
  // Session info
  const totalDuration = sessionConfig?.total_duration || 340;
  const phases = sessionConfig?.phases || [];

  // Initialize volumes from session config
  useEffect(() => {
    if (sessionConfig?.volumes) {
      setVolumes(sessionConfig.volumes);
    }
  }, [sessionConfig]);

  // Initialize Web Audio API for binaural beats
  const initBinaural = useCallback(() => {
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    const binaural = sessionConfig?.binaural || { carrier_freq: 200, beat_freq: 10 };
    
    // Left ear oscillator
    binauralOscillatorL.current = audioContext.createOscillator();
    binauralGainL.current = audioContext.createGain();
    binauralOscillatorL.current.frequency.value = binaural.carrier_freq;
    binauralOscillatorL.current.connect(binauralGainL.current);
    
    // Right ear oscillator (slightly different frequency for binaural effect)
    binauralOscillatorR.current = audioContext.createOscillator();
    binauralGainR.current = audioContext.createGain();
    binauralOscillatorR.current.frequency.value = binaural.carrier_freq + binaural.beat_freq;
    binauralOscillatorR.current.connect(binauralGainR.current);
    
    // Create stereo panner
    const pannerL = audioContext.createStereoPanner();
    const pannerR = audioContext.createStereoPanner();
    pannerL.pan.value = -1;
    pannerR.pan.value = 1;
    
    binauralGainL.current.connect(pannerL);
    binauralGainR.current.connect(pannerR);
    pannerL.connect(audioContext.destination);
    pannerR.connect(audioContext.destination);
    
    // Set initial volume
    const vol = volumes.binaural * 0.1; // Binaural should be subtle
    binauralGainL.current.gain.value = vol;
    binauralGainR.current.gain.value = vol;
  }, [sessionConfig, volumes.binaural]);

  // Start binaural beats
  const startBinaural = useCallback(() => {
    if (binauralOscillatorL.current && binauralOscillatorR.current) {
      try {
        binauralOscillatorL.current.start();
        binauralOscillatorR.current.start();
      } catch (e) {
        // Already started
      }
    }
  }, []);

  // Stop binaural beats
  const stopBinaural = useCallback(() => {
    if (binauralOscillatorL.current) {
      try {
        binauralOscillatorL.current.stop();
        binauralOscillatorR.current.stop();
      } catch (e) {}
      binauralOscillatorL.current = null;
      binauralOscillatorR.current = null;
    }
  }, []);

  // Update binaural volume
  useEffect(() => {
    if (binauralGainL.current && binauralGainR.current) {
      const vol = volumes.binaural * 0.1;
      binauralGainL.current.gain.value = vol;
      binauralGainR.current.gain.value = vol;
    }
  }, [volumes.binaural]);

  // Update ambient volume
  useEffect(() => {
    if (ambientRef.current) {
      ambientRef.current.volume = volumes.ambient;
    }
  }, [volumes.ambient]);

  // Determine current phase based on time
  useEffect(() => {
    const phase = phases.find(p => 
      currentTime >= p.start_time && currentTime < p.end_time
    );
    
    if (phase && phase.phase !== currentPhase) {
      setCurrentPhase(phase.phase);
      onPhaseChange?.(phase.phase);
      
      // Update displayed text based on phase
      if (phase.phase === 'arrival') {
        setDisplayedText(sessionConfig?.guidance?.arrival || 'Welcome. Find a comfortable position...');
      } else if (phase.phase === 'guidance') {
        setDisplayedText(sessionConfig?.guidance?.transition || 'Let these words sink deep...');
      } else if (phase.phase === 'integration') {
        setDisplayedText(sessionConfig?.guidance?.integration || 'Take a moment to feel the shift...');
      } else if (phase.phase === 'action_anchor') {
        setDisplayedText(sessionConfig?.guidance?.anchor || challengeContext?.closing_message || 'Carry this feeling with you...');
      }
    }
  }, [currentTime, phases, currentPhase, sessionConfig, challengeContext, onPhaseChange]);

  // Affirmation cycling during affirmation_loop phase
  useEffect(() => {
    if (currentPhase === 'affirmation_loop' && isPlaying && affirmations.length > 0) {
      // Display current affirmation
      setDisplayedText(affirmations[currentAffirmationIndex]);
      
      // Cycle to next affirmation
      const pauseDuration = sessionConfig?.affirmation_settings?.pause_between || 4;
      affirmationTimerRef.current = setTimeout(() => {
        setCurrentAffirmationIndex(prev => 
          (prev + 1) % affirmations.length
        );
      }, pauseDuration * 1000);
      
      return () => {
        if (affirmationTimerRef.current) {
          clearTimeout(affirmationTimerRef.current);
        }
      };
    }
  }, [currentPhase, isPlaying, currentAffirmationIndex, affirmations, sessionConfig]);

  // Main playback timer
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= totalDuration) {
            setIsPlaying(false);
            onComplete?.();
            return totalDuration;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying, totalDuration, onComplete]);

  // Play/Pause handler
  const togglePlay = async () => {
    if (!isPlaying) {
      // Initialize and start binaural
      initBinaural();
      startBinaural();
      
      // Start ambient
      if (ambientRef.current) {
        ambientRef.current.play().catch(() => {});
      }
      
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
      
      if (ambientRef.current) {
        ambientRef.current.pause();
      }
    }
  };

  // Reset session
  const resetSession = () => {
    setCurrentTime(0);
    setCurrentAffirmationIndex(0);
    setCurrentPhase('arrival');
    setIsPlaying(false);
    stopBinaural();
    if (ambientRef.current) {
      ambientRef.current.currentTime = 0;
      ambientRef.current.pause();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopBinaural();
      if (timerRef.current) clearInterval(timerRef.current);
      if (affirmationTimerRef.current) clearTimeout(affirmationTimerRef.current);
    };
  }, [stopBinaural]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get phase display name
  const getPhaseDisplay = (phase) => {
    const names = {
      arrival: 'Arrival',
      guidance: 'Guidance',
      affirmation_loop: 'Affirmations',
      integration: 'Integration',
      action_anchor: 'Action Anchor'
    };
    return names[phase] || phase;
  };

  // Volume slider component
  const VolumeSlider = ({ label, icon: Icon, value, onChange, color }) => (
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-slate-400 w-20">{label}</span>
      <input
        type="range"
        min="0"
        max="100"
        value={value * 100}
        onChange={(e) => onChange(e.target.value / 100)}
        className="flex-1 h-1 bg-white/20 rounded-full appearance-none cursor-pointer
                   [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 
                   [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full 
                   [&::-webkit-slider-thumb]:bg-white"
      />
      <span className="text-xs text-slate-500 w-8">{Math.round(value * 100)}%</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 z-50 flex flex-col">
      {/* Hidden ambient audio element */}
      <audio
        ref={ambientRef}
        src={`${API_URL}/api/audio/ambient/${sessionConfig?.ambient?.sound?.file || 'ambient_rain.mp3'}`}
        loop
        preload="auto"
      />
      
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-12 pb-4">
        <button 
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="close-audio-engine"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        
        <div className="text-center">
          <p className="text-xs text-indigo-400 uppercase tracking-wider">
            {sessionConfig?.mode?.replace('_', ' ') || 'Flow'} Mode
          </p>
          {challengeContext && (
            <p className="text-xs text-slate-400 mt-1">{challengeContext.day_title}</p>
          )}
        </div>
        
        <button 
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="audio-settings-btn"
        >
          <Settings className="w-5 h-5 text-white" />
        </button>
      </div>
      
      {/* Phase indicator */}
      <div className="px-6 mb-4">
        <div className="flex gap-1">
          {['arrival', 'guidance', 'affirmation_loop', 'integration', 'action_anchor'].map((phase, i) => (
            <div
              key={phase}
              className={`flex-1 h-1 rounded-full transition-all duration-500 ${
                phase === currentPhase 
                  ? 'bg-indigo-500' 
                  : phases.findIndex(p => p.phase === phase) < phases.findIndex(p => p.phase === currentPhase)
                  ? 'bg-indigo-500/50'
                  : 'bg-white/10'
              }`}
            />
          ))}
        </div>
        <p className="text-center text-xs text-indigo-400 mt-2">
          {getPhaseDisplay(currentPhase)}
        </p>
      </div>
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        {/* Visual feedback - breathing circle */}
        <div className={`w-48 h-48 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 
                        border border-indigo-500/30 flex items-center justify-center mb-8
                        ${isPlaying ? 'animate-pulse' : ''}`}>
          <div className={`w-36 h-36 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/30 
                          flex items-center justify-center transition-all duration-1000
                          ${isPlaying ? 'scale-105' : 'scale-100'}`}>
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500/50 to-purple-500/50 
                          flex items-center justify-center">
              {currentPhase === 'affirmation_loop' ? (
                <Mic className="w-8 h-8 text-indigo-300" />
              ) : (
                <Waves className="w-8 h-8 text-indigo-300" />
              )}
            </div>
          </div>
        </div>
        
        {/* Current text display */}
        <div className="text-center max-w-sm min-h-[80px]">
          <p className={`text-lg text-white/90 leading-relaxed transition-opacity duration-500
                        ${displayedText ? 'opacity-100' : 'opacity-0'}`}>
            {displayedText}
          </p>
          {challengeContext?.theme && currentPhase === 'arrival' && (
            <p className="text-sm text-indigo-400 mt-3 italic">
              "{challengeContext.theme}"
            </p>
          )}
        </div>
      </div>
      
      {/* Settings panel */}
      {showSettings && (
        <div className="absolute inset-x-0 bottom-32 mx-6 bg-slate-800/95 backdrop-blur-sm 
                       rounded-2xl p-5 border border-white/10 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-white">Layer Controls</h3>
            <button 
              onClick={() => setShowSettings(false)}
              className="text-slate-400 hover:text-white"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>
          
          <VolumeSlider
            label="Ambient"
            icon={Wind}
            value={volumes.ambient}
            onChange={(v) => setVolumes(prev => ({ ...prev, ambient: v }))}
            color="text-emerald-400"
          />
          <VolumeSlider
            label="Binaural"
            icon={Brain}
            value={volumes.binaural}
            onChange={(v) => setVolumes(prev => ({ ...prev, binaural: v }))}
            color="text-purple-400"
          />
          <VolumeSlider
            label="Subliminal"
            icon={MessageCircle}
            value={volumes.subliminal}
            onChange={(v) => setVolumes(prev => ({ ...prev, subliminal: v }))}
            color="text-blue-400"
          />
          <VolumeSlider
            label="Voice"
            icon={Mic}
            value={volumes.voice}
            onChange={(v) => setVolumes(prev => ({ ...prev, voice: v }))}
            color="text-amber-400"
          />
          <VolumeSlider
            label="Guidance"
            icon={Volume2}
            value={volumes.guidance}
            onChange={(v) => setVolumes(prev => ({ ...prev, guidance: v }))}
            color="text-cyan-400"
          />
          
          <p className="text-[10px] text-slate-500 text-center pt-2 border-t border-white/10">
            Use headphones for binaural beat effect
          </p>
        </div>
      )}
      
      {/* Controls */}
      <div className="px-6 pb-8">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-1000"
              style={{ width: `${(currentTime / totalDuration) * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-400">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(totalDuration)}</span>
          </div>
        </div>
        
        {/* Play controls */}
        <div className="flex items-center justify-center gap-6">
          <button
            onClick={resetSession}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="reset-session-btn"
          >
            <RotateCcw className="w-5 h-5 text-white" />
          </button>
          
          <button
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 
                     flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
            data-testid="play-pause-btn"
          >
            {isPlaying ? (
              <Pause className="w-7 h-7 text-white" />
            ) : (
              <Play className="w-7 h-7 text-white ml-1" />
            )}
          </button>
          
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            {showSettings ? (
              <ChevronDown className="w-5 h-5 text-white" />
            ) : (
              <ChevronUp className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioEngine;
